// Permisos de administrador por sección: Oculto / Ver / Editar (o Sí/No donde no hay edición).
// Se guardan como claves booleanas: <clave> = puede ver, <clave>_edit = puede modificar.

export const GROUPS = [
  ['Día a día', [
    { id: 'dashboard', label: 'Panel de hoy', desc: 'Quién vino y quién faltó hoy', view: 'dashboard', edit: null },
    { id: 'records', label: 'Registros', desc: 'Historial de asistencia; editar y cargar a mano', view: 'records', edit: 'records_edit' },
    { id: 'extra_hours', label: 'Horas extra', desc: 'Ver y aprobar horas extra', view: 'extra_hours', edit: 'extra_hours_edit' },
    { id: 'alertas', label: 'Alertas y notificaciones', desc: 'Avisos del sistema; activar y desactivar alertas', view: 'notifications', edit: 'notifications_edit' },
  ]],
  ['Personas', [
    { id: 'employees', label: 'Empleados', desc: 'Lista y datos; crear, editar y dar de baja', view: 'employees', edit: 'employees_edit' },
    { id: 'vacaciones', label: 'Vacaciones', desc: 'Ver solicitudes; aprobar y editar', view: 'vacaciones', edit: 'vacaciones_edit' },
    { id: 'licencias', label: 'Licencias', desc: 'Ver licencias; cargar y aprobar', view: 'licencias', edit: 'licencias_edit' },
    { id: 'exceptions', label: 'Excepciones de horario', desc: 'Ver y gestionar excepciones y convocatorias', view: 'exceptions', edit: 'exceptions_edit' },
    { id: 'tareas', label: 'Tareas', desc: 'Ver tareas; crear y eliminar', view: 'tareas', edit: 'tareas_edit' },
    { id: 'holidays', label: 'Feriados', desc: 'Ver feriados; importar y editar', view: 'holidays', edit: 'holidays_edit' },
  ]],
  ['Dinero', [
    { id: 'payroll', label: 'Sueldos y liquidaciones', desc: 'Ver sueldos; editar, aprobar y marcar pagos', view: 'salaries', edit: 'salaries_edit' },
  ]],
  ['Análisis y ajustes', [
    { id: 'analysis', label: 'Análisis', desc: 'Estadísticas y tendencias', view: 'analysis', edit: null },
    { id: 'export', label: 'Exportar', desc: 'Descargar CSV y PDF', view: 'export', edit: null },
    { id: 'settings', label: 'Reglas y configuración', desc: 'Tolerancias, reglas, formas de registrar, perfiles por hora', view: 'settings', edit: null, yesNo: 'Acceso' },
  ]],
];
export const SECTIONS = GROUPS.flatMap((g) => g[1]);
// Lista plana de claves (la usa "super admin" para darle todo)
export const ALL_PERMISSIONS = SECTIONS.flatMap((s) => [{ key: s.view }, ...(s.edit ? [{ key: s.edit }] : [])]);

// Secciones que antes eran visibles para todo administrador: si nunca se configuraron, siguen abiertas.
const OPEN = new Set(['vacaciones', 'vacaciones_edit', 'tareas', 'tareas_edit', 'licencias', 'licencias_edit', 'extra_hours', 'extra_hours_edit']);
// Claves de edición nuevas que heredan el valor de la clave de ver si nunca se configuraron.
const FALLBACK = { holidays_edit: 'holidays', exceptions_edit: 'exceptions', notifications_edit: 'notifications' };

export function permOf(perms, key) {
  if (!perms) return true; // administrador antiguo = acceso total
  if (perms[key] !== undefined) return !!perms[key];
  if (OPEN.has(key)) return true;
  if (FALLBACK[key]) return !!perms[FALLBACK[key]];
  return false;
}

export function hasPermission(profile, key) {
  if (!profile) return false;
  if (profile.role !== 'admin') return false;
  if (profile.is_super_admin) return true;
  return permOf(profile.admin_permissions, key);
}

// 0 = oculto, 1 = ver, 2 = editar
export const levelOf = (perms, sec) => {
  if (!permOf(perms, sec.view)) return 0;
  if (sec.edit && permOf(perms, sec.edit)) return 2;
  return 1;
};
export const withLevel = (perms, sec, lvl) => {
  const next = { ...perms };
  next[sec.view] = lvl >= 1;
  if (sec.edit) next[sec.edit] = lvl >= 2;
  return next;
};
// Completa todas las claves de forma explícita (para guardar un perfil sin ambigüedades)
export const materialize = (perms) => {
  let out = {};
  SECTIONS.forEach((s) => { out = withLevel(out, s, levelOf(perms, s)); });
  return out;
};
export const fromLevels = (levels) => {
  let out = {};
  SECTIONS.forEach((s) => { out = withLevel(out, s, Math.min(levels[s.id] ?? 0, s.edit ? 2 : 1)); });
  return out;
};
export const summarize = (perms) => {
  const lv = SECTIONS.map((s) => levelOf(perms, s));
  return { visible: lv.filter((l) => l >= 1).length, editable: lv.filter((l) => l === 2).length, total: SECTIONS.length };
};

const all = (n) => Object.fromEntries(SECTIONS.map((s) => [s.id, n]));
// Plantillas para empezar un perfil
export const PERMISSION_PRESETS = [
  { name: 'Supervisor', desc: 'Ve la asistencia y la corrige. Sin sueldos, exportes ni altas y bajas.',
    perms: fromLevels({ ...all(0), dashboard: 1, records: 2, extra_hours: 1, alertas: 1, employees: 1, vacaciones: 1, licencias: 1, exceptions: 2, tareas: 1, holidays: 1, analysis: 1 }) },
  { name: 'Recursos Humanos', desc: 'Gestiona personas, licencias, vacaciones y horarios. Sin sueldos ni configuración.',
    perms: fromLevels({ ...all(2), payroll: 0, settings: 0, export: 1, analysis: 1, dashboard: 1 }) },
  { name: 'Contador', desc: 'Sueldos y liquidaciones con edición; el resto solo lectura.',
    perms: fromLevels({ ...all(0), dashboard: 1, records: 1, employees: 1, extra_hours: 1, payroll: 2, analysis: 1, export: 1, holidays: 1 }) },
  { name: 'Solo lectura', desc: 'Ve casi todo, no modifica nada. Sin sueldos ni configuración.',
    perms: fromLevels({ ...all(1), payroll: 0, settings: 0 }) },
  { name: 'Acceso total', desc: 'Todas las secciones con edición (sin ser super admin).',
    perms: fromLevels(all(2)) },
];

const OPTS = (sec) => (sec.edit ? [[0, 'Oculto'], [1, 'Ver'], [2, 'Editar']] : [[0, 'Oculto'], [1, sec.yesNo || 'Ver']]);
const TONE = { 0: 'bg-gray-700 text-white', 1: 'bg-sky-600 text-white', 2: 'bg-emerald-600 text-white' };

export function AdminPermissionsEditor({ value, onChange, compact }) {
  const perms = value || {};
  const set = (sec, lvl) => onChange(withLevel(materialize(perms), sec, lvl));
  const setAll = (lvl) => onChange(fromLevels(Object.fromEntries(SECTIONS.map((s) => [s.id, lvl]))));
  const sum = summarize(perms);
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Permisos por sección</p>
          <p className="text-[11px] text-gray-400">{sum.visible} de {sum.total} visibles · {sum.editable} con edición</p>
        </div>
        <div className="flex gap-1.5">
          {[[0, 'Todo oculto'], [1, 'Todo ver'], [2, 'Todo editar']].map(([l, t]) => (
            <button key={l} type="button" onClick={() => setAll(l)} className="text-[11px] font-bold px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200">{t}</button>
          ))}
        </div>
      </div>
      {GROUPS.map(([gname, secs]) => (
        <div key={gname}>
          <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">{gname}</p>
          <div className="space-y-1.5">
            {secs.map((sec) => {
              const lvl = levelOf(perms, sec);
              return (
                <div key={sec.id} className={`rounded-2xl border p-3 ${lvl === 0 ? 'border-gray-100 bg-gray-50/60' : 'border-sky-100 bg-white'}`}>
                  <div className={`flex ${compact ? 'flex-col' : 'flex-col sm:flex-row sm:items-center'} gap-2`}>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-bold ${lvl === 0 ? 'text-gray-400' : 'text-gray-800'}`}>{sec.label}</p>
                      <p className="text-[11px] text-gray-400 leading-snug">{sec.desc}</p>
                    </div>
                    <div className="inline-flex rounded-xl bg-gray-100 p-0.5 self-start sm:self-auto flex-shrink-0">
                      {OPTS(sec).map(([v, t]) => (
                        <button key={v} type="button" onClick={() => set(sec, v)}
                          className={`px-3 py-1.5 rounded-[10px] text-xs font-bold transition-colors ${lvl === v ? TONE[v] : 'text-gray-500 hover:text-gray-700'}`}>{t}</button>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
